import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import type { MeasurementEndpointIdentity } from "@/utils/annotationMeasurementUtils";
import { SEGMENTATION_FILTER_DISPLAY, type SegmentationFilterId } from "../annotationViewSettings";

/** A boolean overlay toggle (mask, collapse-to-points, …) and its change handler. */
type ToggleConfig = { value: boolean; onChange: (value: boolean) => void };

const SECTION_HEADER_CLASS = "text-sm font-semibold text-gray-900";
// A top divider between sections; `first:` drops it (and the padding) on whichever
// section renders first, so the top-most control has no stray line above it.
const SECTION_WRAPPER_CLASS = "border-t border-gray-200 pt-3 first:border-t-0 first:pt-0";

/** A single keyboard-key cap. */
const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] leading-none text-gray-700">
    {children}
  </kbd>
);

/** One "action → keys/gesture" row in the Shortcuts section. */
const ShortcutRow = ({ label, keys }: { label: string; keys: ReactNode }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-gray-600">{label}</span>
    <span className="flex shrink-0 items-center gap-1">{keys}</span>
  </div>
);

/**
 * One landmark under its axis: its own colour, its role, and the exact name that
 * gets persisted and trained on.
 *
 * A real `<button>` rather than a hover-only div, so the highlight is reachable by
 * keyboard. `type="button"` matters — an implicit submit inside a form would be a
 * navigation, not a highlight.
 */
const LandmarkRow = ({
  endpoint,
  dimmed,
  interactive,
  onHighlight,
}: {
  endpoint: MeasurementEndpointIdentity;
  dimmed: boolean;
  interactive: boolean;
  onHighlight?: (keypointName: string | null) => void;
}) => {
  const body = (
    <>
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: endpoint.color }}
      />
      <span className={dimmed ? "text-gray-400" : undefined}>{endpoint.endpointLabel}</span>
      <span className="truncate font-mono text-[11px] text-gray-400">{endpoint.name}</span>
    </>
  );
  const layout = "flex w-full items-center gap-2 rounded px-1 py-0.5 text-left";

  if (!interactive) {
    return (
      <div
        className={`${layout} text-gray-400`}
        title={endpoint.label}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      // Both halves, because the persisted name is the authoritative one and a
      // screen reader would otherwise never hear it — `aria-label` replaces the
      // descendant text that shows it sighted.
      aria-label={`${endpoint.label}, ${endpoint.name}`}
      className={`${layout} hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none`}
      onMouseEnter={() => onHighlight?.(endpoint.name)}
      onMouseLeave={() => onHighlight?.(null)}
      onFocus={() => onHighlight?.(endpoint.name)}
      onBlur={() => onHighlight?.(null)}>
      {body}
    </button>
  );
};

/** One checkbox toggle in the Display section (Mask, Collapse boxes to points, …). */
const ToggleRow = ({ label, toggle }: { label: string; toggle: ToggleConfig }) => (
  <label className="flex cursor-pointer items-center gap-2">
    <Checkbox
      checked={toggle.value}
      onCheckedChange={(checked) => toggle.onChange(checked === true)}
      aria-label={label}
    />
    <span>{label}</span>
  </label>
);

interface SliderConfig {
  value: number;
  inactive?: boolean;
  onChange: (value: number) => void;
}

/** One measurement axis (e.g. Length, Core Length) the user can park while editing. */
interface MeasurementToggle {
  key: string;
  label: string;
  /** Swatch colour, matching the line drawn on the canvas. */
  color: string;
  hidden: boolean;
  /** The axis's two landmarks, start first. The retraining pipeline learns these by
   *  name, so the reviewer needs to see which dot carries which one. */
  endpoints?: MeasurementEndpointIdentity[];
}

interface RangeSliderConfig extends SliderConfig {
  max: number;
}

interface AnnotationSidebarProps {
  isEditMode?: boolean;
  /** Optional class legend/filter controls, shown before edit instructions. */
  classControls?: ReactNode;
  /** Stats block (e.g. "{n} objects" / "{n} plants"), shown whenever provided. */
  stats?: ReactNode;
  /**
   * View-only mask show/hide toggle; when provided, renders the Display section
   * in both preview and edit mode. Never affects downloads/exports.
   */
  maskToggle?: ToggleConfig;
  /**
   * Edit-only toggle that collapses every box to a center point (and drops
   * masks/keypoints) so a crowded image is legible. Also bound to Ctrl+B.
   */
  collapseToggle?: ToggleConfig;
  /** Edit-only view setting for annotations beyond the persisted measurement area. */
  outsideBoundaryToggle?: ToggleConfig;
  /** Review-only display preference for keypoint stand counts. */
  pointSizeSlider?: SliderConfig;
  /** Edit-mode instruction hints; only shown in edit mode. */
  instructions?: ReactNode;
  /** Per-axis show/hide toggles; only shown in edit mode. */
  measurementToggles?: MeasurementToggle[];
  onToggleMeasurement?: (axisKey: string) => void;
  /**
   * Point the canvas at one landmark while the reviewer hovers or focuses its row.
   * The highlight targets a single box, so the rows go inert without a selection
   * rather than ringing the same landmark on every annotation in the image.
   */
  onHighlightKeypoint?: (keypointName: string | null) => void;
  /** False parks the landmark rows as read-only text: the canvas highlight targets
   *  one box, so with nothing selected there is nothing to point at. */
  landmarksInteractive?: boolean;
  confidenceSlider?: SliderConfig;
  maxLengthSlider?: RangeSliderConfig;
  maxCameraDistanceSlider?: RangeSliderConfig;
  maxBboxLengthSlider?: RangeSliderConfig;
  maxOverlapIouSlider?: SliderConfig;
  /** When provided, renders a Reset control that returns all filters to inactive. */
  onResetFilters?: () => void;
}

/** Normalized descriptor so every filter renders through one slider block. */
interface SliderDef {
  id: SegmentationFilterId;
  label: string;
  /** A filter is inactive until the user adjusts it — shown dulled, not applied. */
  inactive?: boolean;
  min: number;
  max: number;
  step: number;
  /** Slider thumb position (may differ from the model value, e.g. inverted sensitivity). */
  sliderValue: number;
  /** Formatted read-out shown next to the label. */
  display: string;
  /** Maps the slider thumb position back to the model value. */
  onValueChange: (sliderValue: number) => void;
}

/**
 * Right-hand control panel for the annotation editor. Holds the object/plant
 * stats, an Instructions section, and a Filters section that lists every
 * available filter with its own slider. Rendering this as a real sidebar
 * (rather than an overlay on the canvas) keeps the controls from occluding the
 * photo.
 */
export const AnnotationSidebar = ({
  isEditMode,
  classControls,
  stats,
  maskToggle,
  collapseToggle,
  outsideBoundaryToggle,
  pointSizeSlider,
  instructions,
  measurementToggles,
  onToggleMeasurement,
  onHighlightKeypoint,
  landmarksInteractive,
  confidenceSlider,
  maxLengthSlider,
  maxCameraDistanceSlider,
  maxBboxLengthSlider,
  maxOverlapIouSlider,
  onResetFilters,
}: AnnotationSidebarProps) => {
  const sliderDefs: SliderDef[] = [
    confidenceSlider && {
      id: "sensitivity",
      label: SEGMENTATION_FILTER_DISPLAY.sensitivity.sliderLabel,
      inactive: confidenceSlider.inactive,
      min: 0,
      max: 1,
      step: 0.01,
      // Sensitivity thumb is inverted; the table's format applies the same inversion
      // to the MODEL value, so display and thumb stay in agreement.
      sliderValue: 1 - confidenceSlider.value,
      display: SEGMENTATION_FILTER_DISPLAY.sensitivity.format(confidenceSlider.value),
      onValueChange: (val) => confidenceSlider.onChange(1 - val),
    },
    maxLengthSlider && {
      id: "maxLength",
      label: SEGMENTATION_FILTER_DISPLAY.maxLength.sliderLabel,
      inactive: maxLengthSlider.inactive,
      min: 0,
      max: maxLengthSlider.max,
      step: 0.01,
      sliderValue: maxLengthSlider.value,
      display: SEGMENTATION_FILTER_DISPLAY.maxLength.format(maxLengthSlider.value),
      onValueChange: (val) => maxLengthSlider.onChange(val),
    },
    maxCameraDistanceSlider && {
      id: "cameraDistance",
      label: SEGMENTATION_FILTER_DISPLAY.cameraDistance.sliderLabel,
      inactive: maxCameraDistanceSlider.inactive,
      min: 0,
      max: maxCameraDistanceSlider.max,
      step: 0.01,
      sliderValue: maxCameraDistanceSlider.value,
      display: SEGMENTATION_FILTER_DISPLAY.cameraDistance.format(maxCameraDistanceSlider.value),
      onValueChange: (val) => maxCameraDistanceSlider.onChange(val),
    },
    maxBboxLengthSlider && {
      id: "bboxLength",
      label: SEGMENTATION_FILTER_DISPLAY.bboxLength.sliderLabel,
      inactive: maxBboxLengthSlider.inactive,
      min: 0,
      max: maxBboxLengthSlider.max,
      step: 1,
      sliderValue: maxBboxLengthSlider.value,
      display: SEGMENTATION_FILTER_DISPLAY.bboxLength.format(maxBboxLengthSlider.value),
      onValueChange: (val) => maxBboxLengthSlider.onChange(val),
    },
    maxOverlapIouSlider && {
      id: "maxOverlapIou",
      label: SEGMENTATION_FILTER_DISPLAY.maxOverlapIou.sliderLabel,
      inactive: maxOverlapIouSlider.inactive,
      min: 0,
      max: 1,
      step: 0.01,
      sliderValue: maxOverlapIouSlider.value,
      display: SEGMENTATION_FILTER_DISPLAY.maxOverlapIou.format(maxOverlapIouSlider.value),
      onValueChange: (val) => maxOverlapIouSlider.onChange(val),
    },
  ].filter(Boolean) as SliderDef[];

  const anyFilterActive = sliderDefs.some((slider) => !slider.inactive);

  // After a pointer adjustment, drop focus from the slider thumb so Left/Right arrows
  // navigate to the prev/next image instead of nudging the filter value (Radix keeps
  // the thumb focused after a click/drag). Keyboard users who Tab to a slider keep
  // focus and can still adjust with arrows.
  const releaseSliderFocusAfterPointer = () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.getAttribute("role") === "slider") active.blur();
  };

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col gap-4 overflow-y-auto border-l border-gray-200 bg-white p-4 text-sm text-gray-700">
      {/* Everything that toggles what's overlaid on the image, in one section at the
          very top — this is what reviewers reach for most. These are edit-only curation
          controls: in preview the top-bar "Hide Annotations" already covers coarse
          show/hide, so the whole section (Mask included) stays out of preview. */}
      {isEditMode &&
        (maskToggle ||
          pointSizeSlider ||
          outsideBoundaryToggle ||
          collapseToggle ||
          (measurementToggles && measurementToggles.length > 0)) && (
          <div className={SECTION_WRAPPER_CLASS}>
            <h3 className={`mb-2 ${SECTION_HEADER_CLASS}`}>Display</h3>
            <div className="space-y-2">
              {pointSizeSlider && (
                <div className="space-y-2 pb-2">
                  <div className="flex items-center justify-between">
                    <span>Point size</span>
                    <span className="tabular-nums">{pointSizeSlider.value}%</span>
                  </div>
                  <Slider
                    thumbLabels={["Point size"]}
                    value={[pointSizeSlider.value]}
                    min={10}
                    max={150}
                    step={5}
                    onValueChange={([value]) => pointSizeSlider.onChange(value)}
                    onPointerUp={releaseSliderFocusAfterPointer}
                  />
                </div>
              )}
              {maskToggle && (
                <ToggleRow
                  label="Mask"
                  toggle={maskToggle}
                />
              )}
              {outsideBoundaryToggle && (
                <ToggleRow
                  label="Show annotations outside measurement area"
                  toggle={outsideBoundaryToggle}
                />
              )}
              {collapseToggle && (
                <ToggleRow
                  label="Collapse boxes to points"
                  toggle={collapseToggle}
                />
              )}
              {measurementToggles?.map((toggle) => (
                <div key={toggle.key}>
                  {/* The label wraps ONLY the checkbox and the axis name. Nesting the
                    landmark rows inside it would make every attempt to locate a
                    landmark park the axis instead. */}
                  <label className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={!toggle.hidden}
                      onCheckedChange={() => onToggleMeasurement?.(toggle.key)}
                      aria-label={toggle.label}
                    />
                    {/* A bar for the line, dots for its endpoints — so "which swatch is
                      the line and which is a point" reads at a glance. */}
                    <span
                      aria-hidden
                      className="h-1 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: toggle.color }}
                    />
                    <span className={toggle.hidden ? "text-gray-400" : undefined}>{toggle.label}</span>
                  </label>
                  {toggle.endpoints && toggle.endpoints.length > 0 && (
                    <div className="mt-1 space-y-0.5 pl-6">
                      {toggle.endpoints.map((endpoint) => (
                        <LandmarkRow
                          key={endpoint.name}
                          endpoint={endpoint}
                          dimmed={toggle.hidden}
                          interactive={Boolean(landmarksInteractive)}
                          onHighlight={onHighlightKeypoint}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {!landmarksInteractive && measurementToggles?.some((toggle) => toggle.endpoints?.length) && (
                <p className="pl-6 text-[11px] text-gray-400">Select a box to locate its landmarks.</p>
              )}
            </div>
          </div>
        )}

      {stats && <div className={`${SECTION_WRAPPER_CLASS} text-gray-900`}>{stats}</div>}

      {classControls && <div className="border-t border-gray-200 pt-3">{classControls}</div>}

      {isEditMode && instructions && (
        <div className={SECTION_WRAPPER_CLASS}>
          <h3 className={`mb-2 ${SECTION_HEADER_CLASS}`}>Instructions</h3>
          <div className="text-gray-700">{instructions}</div>
        </div>
      )}

      {sliderDefs.length > 0 && (
        <div className={SECTION_WRAPPER_CLASS}>
          <div className="mb-1 flex items-center justify-between">
            <h3 className={SECTION_HEADER_CLASS}>Filters</h3>
            {onResetFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onResetFilters}
                disabled={!anyFilterActive}
                className="h-auto px-2 py-1 text-xs font-normal text-gray-500">
                Reset
              </Button>
            )}
          </div>
          <p className="mb-3 text-xs text-gray-400">Filters are inactive until you adjust them.</p>
          <div className="space-y-4">
            {sliderDefs.map((slider) => (
              <div
                key={slider.id}
                className={`space-y-1 ${slider.inactive ? "opacity-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    {slider.label}
                    {slider.inactive && <span className="text-[10px] uppercase tracking-wide text-gray-400">off</span>}
                  </span>
                  <span className="text-gray-500">{slider.display}</span>
                </div>
                <Slider
                  min={slider.min}
                  max={slider.max}
                  step={slider.step}
                  value={[slider.sliderValue]}
                  onValueChange={([val]) => slider.onValueChange(val)}
                  onPointerUp={releaseSliderFocusAfterPointer}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={SECTION_WRAPPER_CLASS}>
        <h3 className={`mb-2 ${SECTION_HEADER_CLASS}`}>Shortcuts</h3>
        <div className="space-y-1.5 text-xs">
          <ShortcutRow
            label="Zoom in"
            keys={
              <>
                <Kbd>⌘/Ctrl</Kbd>
                <Kbd>+</Kbd>
              </>
            }
          />
          <ShortcutRow
            label="Zoom out"
            keys={
              <>
                <Kbd>⌘/Ctrl</Kbd>
                <Kbd>−</Kbd>
              </>
            }
          />
          <ShortcutRow
            label="Zoom (mouse)"
            keys={<span className="text-gray-500">Wheel-click + drag / pinch</span>}
          />
          <ShortcutRow
            label="Pan (when zoomed)"
            keys={<span className="text-gray-500">Drag / scroll</span>}
          />
          <ShortcutRow
            label="Prev / next image"
            keys={
              <>
                <Kbd>←</Kbd>
                <Kbd>→</Kbd>
              </>
            }
          />
          {isEditMode && collapseToggle && (
            <ShortcutRow
              label="Collapse boxes to points"
              keys={
                <>
                  <Kbd>⌘/Ctrl</Kbd>
                  <Kbd>B</Kbd>
                </>
              }
            />
          )}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          <Kbd>Shift</Kbd> <Kbd>+</Kbd> / <Kbd>−</Kbd> also zooms.
        </p>
      </div>
    </aside>
  );
};
